import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

import { Badge, Button, ButtonWithIcon, toast, Tooltip } from '@/components';
import { HouseholdSection } from '@/components/HouseholdSection';
import {
    AddMemberIcon,
    EditIcon,
    InfoIcon,
    TrashIcon,
} from '@/components/Icons';
import { IKid } from '@/shared/interfaces';
import { HouseholdMemberType } from '@/shared/enums';

import { format, R } from '@/features/profile/resources/ProfileResources.resx';
import { ConfirmDeletionModal } from './ConfirmDeletionModal';
import {
    childrenParentalAuthorityStatusAtom,
    profileAtom,
} from '../model/store';
import { useProfile } from '@/features/shared/lib/useProfile';
import { fetchChildrenParentalAuthorityStatusQueryAtom } from '@/features/shared/features/addOrEditChild/model';
import { adolescentMinimumAgeAtom } from '@/caregiverSpace/shared/model';
import {
    ChildModal,
    AddChildrenModal,
} from '@/features/shared/features/addOrEditChild/ui';
import { AddChildrenMode } from '@/features/shared/features/addOrEditChild/lib';

export function MinorChildrenSection() {
    const setProfileAtom = useSetAtom(profileAtom);
    const profile = useAtomValue(profileAtom);

    const { refetch } = useProfile();

    const parentalAuthorityStatuses = useAtomValue(
        childrenParentalAuthorityStatusAtom,
    );

    const setChildrenParentalAuthorityStatus = useSetAtom(
        childrenParentalAuthorityStatusAtom,
    );

    const { refetch: refetchParentalAuthorityStatus } = useAtomValue(
        fetchChildrenParentalAuthorityStatusQueryAtom,
    );

    useEffect(() => {
        if (profile.Kids.length === 0) {
            return;
        }

        refetchParentalAuthorityStatus().then((result) => {
            if (result.data) {
                setChildrenParentalAuthorityStatus(result.data);
            }
        });
    }, [
        profile.Kids.length,
        refetchParentalAuthorityStatus,
        setChildrenParentalAuthorityStatus,
    ]);

    const [deleteModalForKid, setDeleteModalForKid] = useState<IKid>(null);
    const [editModalForKid, setEditModalForKid] = useState<IKid>(null);
    const [showAddChildModal, setShowAddChildModal] = useState<boolean>(false);
    const adolescentMinimumAge = useAtomValue(adolescentMinimumAgeAtom);

    // Refetch and populate the profile atom again
    async function handleRefetch() {
        const updatedProfile = await refetch();

        if (updatedProfile?.error) {
            toast.addToast({
                type: 'error',
                message: R.RefreshDataError,
                autoClose: true,
            });
            return;
        }

        if (updatedProfile?.data) {
            setProfileAtom(updatedProfile.data);

            const statusResult = await refetchParentalAuthorityStatus();

            if (statusResult.data) {
                setChildrenParentalAuthorityStatus(statusResult.data);
            }
        }
    }

    return (
        <HouseholdSection
            title={format(
                R.ChildrenBelowCertainAge,
                adolescentMinimumAge.toString(),
            )}
            action={
                <Button
                    variant="secondary"
                    outline
                    onClick={() => setShowAddChildModal(true)}
                >
                    <AddMemberIcon />
                    {R.AddChild}
                </Button>
            }
            hasChildren={profile.Kids.length > 0}
        >
            {profile.Kids.map((child, index) => {
                const parentalAuthorityStatus = parentalAuthorityStatuses.find(
                    (x) => x.Cpr === child.CPR,
                );

                return (
                    <div
                        key={child.CPR}
                        className={
                            index !== profile.Kids.length - 1
                                ? 'border-b border-(--color-border-muted)'
                                : ''
                        }
                    >
                        <div className="flex items-center justify-between px-6 py-4">
                            <div className="flex items-center gap-2">
                                {!parentalAuthorityStatus?.IsConfirmed && (
                                    <div className="relative z-50">
                                        <Tooltip
                                            content={
                                                R.ParentalAuthorityCannotBeConfirmed
                                            }
                                        >
                                            <InfoIcon className="text-secondary-red" />
                                        </Tooltip>
                                    </div>
                                )}

                                <div className="text-lg font-medium text-gray-900">
                                    {child.User.FullName}
                                </div>
                            </div>

                            <div className="flex items-center">
                                <ButtonWithIcon
                                    variant="ghost"
                                    onClick={() =>
                                        setEditModalForKid({
                                            UID: child.User.UID,
                                            Name: child.User.FullName,
                                            CPR: child.CPR,
                                        })
                                    }
                                    className="ml-auto flex items-center text-[18px] font-bold"
                                >
                                    <EditIcon />
                                    {R.Edit}
                                </ButtonWithIcon>

                                <Button
                                    variant="ghost"
                                    onClick={() =>
                                        setDeleteModalForKid({
                                            UID: child.User.UID,
                                            Name: child.User.FullName,
                                            CPR: child.CPR,
                                        })
                                    }
                                    aria-labelledby="child-name delete-action"
                                    className="min-w-0 self-center!"
                                >
                                    <TrashIcon
                                        id="delete-action"
                                        className="text-secondary-red block cursor-pointer"
                                    />
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            })}

            {deleteModalForKid && (
                <ConfirmDeletionModal
                    kid={deleteModalForKid}
                    title={R.RemoveChild}
                    onClose={() => setDeleteModalForKid(null)}
                    onMutationSuccess={handleRefetch}
                />
            )}

            {editModalForKid && (
                <ChildModal
                    kid={editModalForKid}
                    onClose={() => setEditModalForKid(null)}
                    onMutationSuccess={handleRefetch}
                    mode={{ isEditing: true, isAdding: false }}
                />
            )}

            {showAddChildModal && (
                <AddChildrenModal
                    title={format(
                        R.InviteChildren,
                        adolescentMinimumAge.toString(),
                    )}
                    allowedTypes={[HouseholdMemberType.Child]}
                    mode={AddChildrenMode.Profile}
                    onClose={() => setShowAddChildModal(false)}
                    onChildAdded={() => {
                        setShowAddChildModal(false);
                        handleRefetch();
                    }}
                />
            )}
        </HouseholdSection>
    );
}
